import FuseAnimate from '@/@fuse/core/FuseAnimate';
import FusePageSimple from '@/@fuse/core/FusePageSimple';
import { getHistoryVideo, getRooms } from '@/app/services/liveStreaming/liveStreaming';
import * as Actions from '@/app/store/actions';
import { Portal, useMediaQuery } from '@material-ui/core';
import { ChevronLeft } from '@material-ui/icons';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import HistoryDrawer from './HistoryDrawer';
import { useHistoryVideo } from './HistoryProvider';
import PBJHistoryPlayer from './PBJ/PBJHistoryPlayer';
import KinesisHistoryPlayer from './Kinesis/KinesisHistoryPlayer';
import SalientHistoryPlayer from './Salient/SalientHistoryPlayer';
import '@/app/main/subadmin/livestreaming/live/Live.css';

const History = ({ isPbj }) => {
	const [rooms, setRooms] = useState([]);
	const [selectedDate, setSelectedDate] = useState(dayjs(new Date()).format('YYYY-MM-DD'));
	const [roomData, setRoomData] = useState(null);
	const { setClipLoading } = useHistoryVideo();
	const [activeHourSlot, setActiveHourSlot] = useState(null);
	const [streamData, setStreamData] = useState({});
	const [selectedClip, setSelectedClip] = useState(null);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [startTime, setStartTime] = useState(0);
	const [endTime, setEndTime] = useState(dayjs().minute(0).second(0).format('HH:mm:ss'));
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [playbackTime, setPlaybackTime] = useState(0);
	const [isPlaying, setIsPlaying] = useState(false);
	const containerRef = useRef(null);
	const isIphone = /iPhone|iPod/.test(navigator.userAgent);
	const showFixedDrawer = useMediaQuery('(min-width:1280px)');
	const mdUp = useMediaQuery('(min-width:960px)');
	const dispatch = useDispatch();
	const pageLayout = useRef(null);
	const [roomsPage, setRoomsPage] = useState(1);
	const [roomsLoading, setRoomsLoading] = useState(false);

	useEffect(() => {
		let isMounted = true;
		const fetchRooms = () => {
			setRoomsLoading(true);
			getRooms('', '', roomsPage, 0)
				.then((res) => {
					if (!isMounted) return;

					if (roomsPage === 1) {
						setRoomData(res.data.data[0].room);
					}
					setRooms((prevRooms) => [...prevRooms, ...(res.data.data || [])]);
					if (res.data.current_page < res.data.last_page) {
						setRoomsPage((prevPage) => prevPage + 1);
					} else {
						setRoomsLoading(false);
					}
				})
				.catch(() => {
					setRoomsLoading(false);
					if (!isMounted) return;
					dispatch(
						Actions.showMessage({
							message: 'Failed to fetch data, please refresh',
							variant: 'error',
						}),
					);
				});
		};

		fetchRooms();

		return () => {
			isMounted = false;
		};
	}, [roomsPage, dispatch]);

	const handleHourClick = (hour) => {
		setActiveHourSlot(hour);
		setSelectedClip(null);
		setEndTime(hour.end_time);
	};

	const handleClipSelect = (clip) => {
		setStartTime(clip.start_time);
		setSelectedClip(clip);
		setPlaybackTime(0);
		if (roomData) {
			setClipLoading(true);
			setEndTime(() => clip.end_time); // Set the end time based on the selected clip
			getHistoryVideo(`${selectedDate} ${clip?.start_time}`, `${selectedDate} ${clip?.end_time}`, roomData?.id)
				.then((res) => {
					setStreamData(res?.data);
					setClipLoading(false);
				})
				.catch(() => {
					dispatch(
						Actions.showMessage({
							message: 'Something went wrong please try again',
							autoHideDuration: 1500,
							variant: 'error',
						}),
					);
					setClipLoading(false);
				});
		}
	};

	const handleRoomChange = (e, v) => {
		setRoomData(v.props.value);
		setStartTime(0);
		setActiveHourSlot(null);
		setStreamData(null);
		setSelectedClip(null);
	};

	const handleToggleDrawer = () => {
		setDrawerOpen(!drawerOpen);
	};

	return (
		<div className="z-[-1]">
			<FusePageSimple
				content={
					<>
						<div className="pt-20 pb-64 w-full">
							<br />
							<FuseAnimate animation="transition.slideLeftIn" duration={600}>
								<div className="flex">
									<div className="w-full">
										<div className="flex justify-between">
											<div className="flex items-start flex-col md:flex-row ">
												<h4 className="font-extrabold md:text-pd-lg">
													{roomData ? `${roomData?.name}` : <span className="" />}
												</h4>
												<span className="md:text-pd-lg md:ml-4">
													{selectedClip
														? ` ${mdUp ? ` - ` : ''}${dayjs(`${selectedDate} ${selectedClip?.start_time}`).format(
																'MMMM D, YYYY h:mm A',
															)} - ${dayjs(`${selectedDate} ${selectedClip?.end_time}`).format('h:mm A')}`
														: ''}
												</span>
											</div>
											{!showFixedDrawer ? (
												<button
													className="text-pd-blue font-bold flex items-center"
													onClick={handleToggleDrawer}
													type="button"
												>
													<ChevronLeft className="mb-4" />
													Select Room & Time
												</button>
											) : null}
										</div>

										<div className={`mt-20 w-full max-w-3xl ${showFixedDrawer ? ' pr-360' : ''}`} ref={containerRef}>
											<Portal container={isFullscreen && isIphone ? document.body : containerRef.current}>
												<div
													className={` ${
														isFullscreen ? 'h-full w-full fixed top-0 left-0 z-9999' : 'relative overflow-hidden'
													}`}
												>
													{streamData?.video_type === 'mjpeg' || streamData?.video_type === 'salient-h264' ? (
														<SalientHistoryPlayer
															date={selectedDate}
															room={roomData}
															startTime={startTime}
															streamData={streamData}
															endTime={endTime}
															selectedDate={selectedDate}
														/>
													) : isPbj ? (
														<PBJHistoryPlayer
															date={selectedDate}
															room={roomData}
															startTime={startTime}
															streamData={streamData}
															endTime={endTime}
															selectedDate={selectedDate}
														/>
													) : (
														<KinesisHistoryPlayer
															endTime={endTime}
															isFullscreen={isFullscreen}
															isPlaying={isPlaying}
															playbackTime={playbackTime}
															room={roomData}
															selectedDate={selectedDate}
															setIsFullscreen={setIsFullscreen}
															setIsPlaying={setIsPlaying}
															setPlaybackTime={setPlaybackTime}
															startTime={startTime}
															streamData={streamData}
														/>
													)}
												</div>
											</Portal>
										</div>
									</div>
								</div>
							</FuseAnimate>
						</div>
						<HistoryDrawer
							activeHourSlot={activeHourSlot}
							handleClipSelect={handleClipSelect}
							handleHourClick={handleHourClick}
							handleRoomChange={handleRoomChange}
							isLoading={roomsLoading}
							open={drawerOpen}
							roomData={roomData}
							rooms={rooms}
							selectedClip={selectedClip}
							selectedDate={selectedDate}
							setActiveHourSlot={setActiveHourSlot}
							setDate={setSelectedDate}
							setOpen={setDrawerOpen}
							setSelectedClip={setSelectedClip}
							setSelectedDate={setSelectedDate}
							setStreamUrl={setStreamData}
							showFixedDrawer={showFixedDrawer}
						/>
					</>
				}
				innerScroll
				ref={pageLayout}
			/>
		</div>
	);
};

export default History;
